<script setup lang="ts">
import { defaultFormatter, formatterProviderKey, TypographyComponent } from '@metapic-apps/shared'
import { computed, inject, ref } from 'vue'

import { usePerformanceChart } from '@/api/performance.api'
import MetricSelectControl from '@/components/PerformanceChart/MetricSelectControl.vue'
import PerformanceLineChart from '@/components/PerformanceChart/PerformanceLineChart.vue'
import { isMoneyMetric } from '@/lib/adapters/performance.adapter'
import { usePerformanceFilter } from '@/lib/hooks/performance-filter.hooks'
import type { PerformanceChartFilter, PerformanceChartMetric } from '@/lib/types/performance.types'
import { useAdvertiserStore } from '@/stores/advertiser.stores'
import { LoadingComponent } from '@/ui-library'
const props = defineProps<{ creatorId?: number }>()
const { formatNumber, formatMoney } = inject(formatterProviderKey, defaultFormatter)
const advertiserStore = useAdvertiserStore()
const { advertiserId, dateFrom, dateTo, previousDateFrom, previousDateTo, trafficSource, creatorId } = usePerformanceFilter(
  () => props.creatorId,
)
const metric = ref<PerformanceChartMetric>('clicks')
const filter = computed<PerformanceChartFilter>(() => ({
  dateFrom: dateFrom.value,
  dateTo: dateTo.value,
  previousDateFrom: previousDateFrom.value,
  previousDateTo: previousDateTo.value,
  trafficSource: trafficSource.value,
  metric: metric.value,
  creatorId: creatorId.value,
}))
const { data, isLoading } = usePerformanceChart(filter, advertiserId)
const valueFormatter = computed(() => {
  const currencyObj = advertiserStore.advertiser?.currencyObj
  if (!isMoneyMetric(metric.value) || !currencyObj) {
    return (value: number) => formatNumber(value, 'compact')
  }
  return (value: number) =>
    formatMoney(
      { amount: value, currency: currencyObj.code },
      { notation: 'compact', minimumFractionDigits: 0, maximumFractionDigits: 1, allowZero: true },
    )
})
const tooltipValueFormatter = computed(() => {
  const currencyObj = advertiserStore.advertiser?.currencyObj
  if (!isMoneyMetric(metric.value) || !currencyObj) {
    return formatNumber
  }
  return (value: number) =>
    formatMoney({ amount: value, currency: currencyObj.code }, { minimumFractionDigits: 0, maximumFractionDigits: 2, allowZero: true })
})
</script>
<template>
  <div class="performance-chart">
    <div class="performance-chart__card">
      <div class="performance-chart__header">
        <TypographyComponent variant="headline-small">Performance</TypographyComponent>
        <div class="performance-chart__legend">
          <span class="performance-chart__legend-item">
            <span class="performance-chart__dot performance-chart__dot--current" />
            <TypographyComponent variant="label-small">Current</TypographyComponent>
          </span>
          <span class="performance-chart__legend-item">
            <span class="performance-chart__dot performance-chart__dot--previous" />
            <TypographyComponent variant="label-small">Previous</TypographyComponent>
          </span>
        </div>
        <MetricSelectControl v-model="metric" />
      </div>
      <div class="performance-chart__body">
        <LoadingComponent v-if="isLoading" :size="48" class="performance-chart__loading" />
        <PerformanceLineChart
          v-else
          :labels="data.labels"
          :current="data.current"
          :previous="data.previous"
          :previous-labels="data.previousLabels"
          :value-formatter="valueFormatter"
          :tooltip-value-formatter="tooltipValueFormatter"
        />
      </div>
    </div>
  </div>
</template>
<style scoped lang="scss">
.performance-chart {
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.performance-chart__card {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  border: 1px solid var(--secondary-8);
  border-radius: 16px;
  background: var(--white);
}
.performance-chart__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.performance-chart__legend {
  display: flex;
  align-items: center;
  gap: 16px;
}
.performance-chart__legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  :deep(.label-small) {
    font-size: 11px;
  }
}
.performance-chart__dot {
  width: 12px;
  height: 12px;
  border-radius: 24px;
  &--current {
    background: var(--pink-500);
  }
  &--previous {
    background: rgba(0, 0, 0, 0.16);
  }
}
.performance-chart__body {
  min-height: 320px;
}
.performance-chart__loading {
  min-height: 320px;
}
</style>
